import { useLayoutEffect, useState } from 'react';

import { useJiraContext } from 'src/services/jira';
import { useSession } from 'src/services/session';

import integration from 'src/lib/api/integration';
import jira from 'src/lib/api/jira';
import pagerduty from 'src/lib/api/pagerduty';
import { createLazySingletonContext } from 'src/lib/hooks';

const emptyArray = [];
const initValue = {
  loading: true,
  loadingError: undefined,
  warningMessages: [],
  mappings: [],
  mappingBasedOnSessionDetails: undefined,
};

const listWarnings = (mappings, mappingBasedOnSessionDetails) => {
  let warningMessages = [];
  if (!mappingBasedOnSessionDetails) return emptyArray;
  if (mappings.length > 1) {
    warningMessages.push(`We found ${mappings.length} mappings based on your emails and user ids.
  Maybe your accounts mapped to different users. Please Remove all existing mappings and create a new one`);
  }

  if (mappings.length === 0) {
    warningMessages.push('Yor user accounts are not linked. Please link them for better user experience.');
  }

  if (mappings.length === 1) {
    const m = mappings[0];
    if (m.pd_id !== mappingBasedOnSessionDetails.pd_id) warningMessages.push('PagerDuty user ID does not match your current user ID.');
    if (m.pd_email !== mappingBasedOnSessionDetails.pd_email) warningMessages.push('PagerDuty user email does not match your current email.');
    if (m.pd_displayname !== mappingBasedOnSessionDetails.pd_displayname) warningMessages.push('PagerDuty user display name does not match your current display name.');
    if (m.jira_name !== mappingBasedOnSessionDetails.jira_name) warningMessages.push('Jira user id does not match your current user ID.');
    if (m.jira_account_id !== mappingBasedOnSessionDetails.jira_account_id) warningMessages.push('Jira user account_id does not match your current user ID.');
    if (m.jira_displayname !== mappingBasedOnSessionDetails.jira_displayname) warningMessages.push('Jira user displayname does not match your current displayname.');
    if (m.jira_email !== mappingBasedOnSessionDetails.jira_email) warningMessages.push('Jira user email does not match your current email.');
  }
  if (warningMessages.length === 0) return emptyArray;
  return warningMessages;
};

const loader = async (loggedIn, session, setState) => {
  if (!session) return; // still loading

  if (!loggedIn) {
    // user mapping has no sense
    setState({ ...initValue, loading: false });
    return;
  }

  setState(initValue);

  const [pagerDutyUser, jiraUser] = await Promise.all([
    pagerduty.getMe(),
    jira.getUserByName(session.name),
  ]);

  const mappingBasedOnSessionDetails = {
    pd_id: pagerDutyUser.id || null,
    pd_email: pagerDutyUser.email || null,
    pd_displayname: pagerDutyUser.summary || null,
    pd_html_url: pagerDutyUser.html_url || null,
    pd_avatar_url: pagerDutyUser.avatar_url || null,
    jira_name: jiraUser.name || null,
    jira_account_id: jiraUser.accountId || null,
    jira_displayname: jiraUser.displayName || null,
    jira_html_url: jiraUser.self || null,
    jira_email: jiraUser.emailAddress || null,
    jira_avatar_url: Object.values(jiraUser.avatarUrls || {})[0] || null
  };

  const accountsMapping = await integration.getAccountMapping();
  if (!accountsMapping) {
    // user mapping has no sense when Jira <-> PD accounts are not mapped
    setState({ ...initValue, mappingBasedOnSessionDetails, loading: false });
    return;
  }

  const { user_mappings: mappings } = await integration.getMappedUsers(mappingBasedOnSessionDetails);
  const warningMessages = listWarnings(mappings, mappingBasedOnSessionDetails);

  setState({
    loading: false,
    loadingError: null,
    warningMessages,
    mappings,
    mappingBasedOnSessionDetails,
  });
};

const useContextBody = () => {
  const { loggedIn } = useSession();
  const { session, loading } = useJiraContext();
  const [state, setState] = useState(initValue);

  useLayoutEffect(() => {
    let setStateLocal = setState;
    if (!session && !loading) {
      setStateLocal({ ...initValue, loading: false, loadingError: new Error('Please login to continue') });
    } else {
      loader(loggedIn, session, setStateLocal)
        .catch(loadingError => setStateLocal({ ...initValue, loading: false, loadingError }));
    }

    return () => { setStateLocal = () => {}; };
  }, [loggedIn, session, loading]);

  return state;
};

export const {
  Provider,
  useHook: useMyUserMapping,
  getCurrentValue: getMyUserMapping,
} = createLazySingletonContext(initValue, useContextBody);

export const removeCurrentMapping = async () => {
  const { mappings } = getMyUserMapping();
  return await Promise.all(mappings.map(m => integration.removeUserMapping(m.id)));
};

export const fixCurrentMappings = async () => {
  const { mappingBasedOnSessionDetails, mappings } = getMyUserMapping();
  if (!mappingBasedOnSessionDetails) {
    throw new Error('Can not create user mapping: user must be logged into both Jira and PagerDuty');
  }

  await Promise.all(mappings.map(m => integration.removeUserMapping(m.id)));
  return integration.createUserMapping({ ...mappingBasedOnSessionDetails, created_by: 'self' });
};

